// Fix: mouse wheel doesn't scroll listboxes inside Radix popovers (e.g. the
// country selector in SkyPhoneInput, or SkyDropdown options) when the popover
// is opened from inside a modal SkyDialog/SkyDrawer. Cause: Radix's modal
// dialog uses `react-remove-scroll`, which installs a document-level wheel
// listener that preventDefault()'s events outside the dialog's DOM subtree.
// Popovers portal their panel to document.body, so the listbox is technically
// "outside the dialog" and its wheel events get swallowed. Keyboard nav,
// scrollbar drag and programmatic scroll all still work — only wheel fails.
//
// Safe scoping: we do nothing unless there is at least one open Radix dialog
// on the page (which is what activates react-remove-scroll in the first
// place). On plain pages with no modal, native wheel handling is untouched.

const LISTBOX_SELECTOR = '[data-radix-popper-content-wrapper] [role="listbox"]';
const OPEN_DIALOG_SELECTOR =
  '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

function findScrollableListbox(target: EventTarget | null): HTMLElement | null {
  let node = target as Node | null;
  while (node && node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    if (typeof el.matches === "function" && el.matches(LISTBOX_SELECTOR)) {
      return el;
    }
    node = el.parentNode;
  }
  return null;
}

function isScrollLockActive(): boolean {
  return document.querySelector(OPEN_DIALOG_SELECTOR) !== null;
}

function onWheel(event: WheelEvent) {
  // Only intervene when a modal dialog is open (that's the only case where
  // react-remove-scroll is active and swallowing wheel events). Without this
  // guard we'd manually-scroll every popover listbox on every page, which
  // would bypass the browser's native wheel behavior (deltaMode handling,
  // OS-level acceleration etc.) unnecessarily.
  if (!isScrollLockActive()) return;

  const scrollable = findScrollableListbox(event.target);
  if (!scrollable) return;

  // Normalize delta across wheel modes: PIXEL (0), LINE (1 ≈ 16px), PAGE (2).
  let delta = event.deltaY;
  if (event.deltaMode === 1) delta *= 16;
  else if (event.deltaMode === 2) delta *= scrollable.clientHeight;

  scrollable.scrollTop += delta;
  event.stopPropagation();
  event.preventDefault();
}

let installed = false;

export function installRadixScrollShim() {
  if (installed) return;
  installed = true;
  // Capture phase on window fires before document-level listeners, so we win
  // the race with react-remove-scroll's preventDefault.
  window.addEventListener("wheel", onWheel, { capture: true, passive: false });
}
